// Auth.jsx — Clerk-based auth gate. Wraps the app and shows a sign-in screen
// if the user isn't authenticated. Clerk's <SignIn> component handles the UI.
//
// On sign-in, we wire Clerk's getToken() into the Supabase client so every
// Supabase call is authenticated as the Clerk user.
//
// Why this works: Clerk has a JWT template called "supabase" that signs JWTs
// with Supabase's JWT secret. Supabase RLS reads auth.jwt() and trusts it.

import { useEffect } from "react";
import {
  ClerkProvider,
  SignIn,
  SignedIn,
  SignedOut,
  UserButton,
  useAuth,
  useUser,
} from "@clerk/clerk-react";
import { setClerkTokenGetter, supabaseConfigured } from "../lib/supabase";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

export function AuthGate({ children }) {
  if (!PUBLISHABLE_KEY) return <SetupScreen />;
  if (!supabaseConfigured) return <SetupScreen supabaseMissing />;

  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
      <SignedOut>
        <SignInScreen />
      </SignedOut>
      <SignedIn>
        <ClerkBridge>{children}</ClerkBridge>
      </SignedIn>
    </ClerkProvider>
  );
}

// When signed in, wire Clerk's JWT getter into the Supabase client so
// every Supabase request is authenticated as the Clerk user.
function ClerkBridge({ children }) {
  const { getToken, isLoaded } = useAuth();

  useEffect(() => {
    if (!isLoaded) return;
    setClerkTokenGetter(async () => {
      try {
        return await getToken({ template: "supabase" });
      } catch (e) {
        console.warn("Clerk token fetch failed", e);
        return null;
      }
    });
    return () => setClerkTokenGetter(null);
  }, [isLoaded, getToken]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-[0.18em] text-ink-400">
          loading…
        </div>
      </div>
    );
  }

  return children;
}

function SignInScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-pronto-page font-sf">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <div
              className="relative w-12 h-12 rounded-[13px] grid place-items-center shadow-sm"
              style={{ background: "linear-gradient(160deg,#26262A,#000)" }}
            >
              <span className="font-round font-bold text-white text-[27px] leading-none tracking-[-0.04em]">p</span>
              <span className="absolute bottom-[9px] right-[10px] w-[6px] h-[6px] rounded-full bg-pronto-navy-light" />
            </div>
            <div className="font-round text-3xl font-bold text-pronto-ink tracking-[-0.02em]">Pronto</div>
          </div>
          <p className="text-sm text-pronto-text-2">Your kitchen, ready.</p>
        </div>
        <SignIn
          appearance={{
            elements: {
              rootBox: "w-full",
              card: "shadow-none border border-[var(--p-sep-12)] bg-white/60",
              headerTitle: "font-round",
              formButtonPrimary: "!bg-pronto-navy hover:!bg-pronto-navy-deep",
            },
          }}
        />
      </div>
    </div>
  );
}

function SetupScreen({ supabaseMissing }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-pronto-page font-sf">
      <div className="max-w-md text-center">
        <div className="text-3xl mb-4">⚙️</div>
        <h1 className="font-round font-bold text-2xl text-pronto-ink mb-3">Setup needed</h1>
        <p className="text-sm text-pronto-text-2 mb-4">
          {supabaseMissing
            ? "Pronto needs a Supabase URL and anon key. "
            : "Pronto needs a Clerk publishable key. "}
          Add these to your <code className="font-mono bg-white px-1 rounded">.env</code> file:
        </p>
        <pre className="text-left text-[12px] bg-pronto-ink text-white p-3 rounded-lg font-mono">
{`VITE_CLERK_PUBLISHABLE_KEY=pk_...
VITE_SUPABASE_URL=https://...
VITE_SUPABASE_ANON_KEY=ey...`}
        </pre>
        <p className="text-xs text-ink-400 mt-4">
          See <code className="font-mono bg-parchment-200 px-1 rounded">cucina-setup-guide.md</code> for setup steps.
        </p>
      </div>
    </div>
  );
}

// Sign-out button — uses Clerk's UserButton which includes profile + sign-out.
export function SignOutButton() {
  return (
    <UserButton
      afterSignOutUrl="/"
      appearance={{
        elements: {
          userButtonAvatarBox: "w-7 h-7",
        },
      }}
    />
  );
}

// Hook for components that need the current user
export function useCurrentUser() {
  const { user, isLoaded } = useUser();
  return {
    user,
    userId: user?.id || null,
    email: user?.primaryEmailAddress?.emailAddress || null,
    ready: isLoaded,
  };
}
